import { useMemo } from "react";
import { ChevronRight, RotateCcw, Layers, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import type { Milestone } from "@/lib/milestones";

export interface MilestoneTimelineProps {
  milestones: Milestone[];
  activeStepNumber?: number | null;
  selectedMilestoneId?: string | null;
  onSelectMilestone?: (milestone: Milestone) => void;
  onClearFilter?: () => void;
  className?: string;
}

export function MilestoneTimeline({
  milestones,
  activeStepNumber,
  selectedMilestoneId,
  onSelectMilestone,
  onClearFilter,
  className,
}: MilestoneTimelineProps) {
  const revisitCount = useMemo(
    () => milestones.filter((m) => m.isRevisit).length,
    [milestones],
  );

  if (milestones.length === 0) return null;

  return (
    <nav
      aria-label="Milestone journey timeline"
      className={cn("rounded-lg border border-border bg-card p-3", className)}
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
            <Layers className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
            <span>Milestone Journey</span>
          </div>
          <span className="text-xs text-muted-foreground">
            &middot; {milestones.length}{" "}
            {milestones.length === 1 ? "surface" : "surfaces"}
          </span>
          {revisitCount > 0 && (
            <span className="flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary-ink">
              <RotateCcw className="h-3 w-3" aria-hidden="true" />
              {revisitCount} {revisitCount === 1 ? "revisit" : "revisits"}
            </span>
          )}
        </div>

        {selectedMilestoneId && onClearFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilter}
            className="h-6 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3" aria-hidden="true" />
            Show all steps
          </Button>
        )}
      </div>

      {/* Horizontal milestone breadcrumb trail */}
      <ol className="flex items-center gap-1.5 overflow-x-auto pb-1 pt-0.5">
        {milestones.map((milestone, index) => {
          const isSelected = selectedMilestoneId === milestone.id;
          const isStepActive =
            !selectedMilestoneId &&
            activeStepNumber != null &&
            milestone.stepNumbers.includes(activeStepNumber);
          const isHighlighted = isSelected || isStepActive;

          return (
            <li
              key={milestone.id}
              className="flex shrink-0 items-center gap-1.5"
            >
              <button
                type="button"
                onClick={() => onSelectMilestone?.(milestone)}
                aria-current={isHighlighted ? "step" : undefined}
                className={cn(
                  "group relative flex flex-col items-start rounded-md border px-2.5 py-1.5 text-left transition-all",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
                  isHighlighted
                    ? "border-primary bg-primary/10 shadow-xs"
                    : "border-border bg-background hover:border-muted-foreground hover:bg-muted",
                )}
              >
                <div className="flex w-full items-center justify-between gap-2">
                  <span
                    className={cn(
                      "text-xs font-medium tabular-nums",
                      isHighlighted
                        ? "font-semibold text-primary"
                        : "text-muted-foreground",
                    )}
                  >
                    {milestone.stepCount === 1
                      ? `Step ${milestone.startStepNumber}`
                      : `Steps ${milestone.startStepNumber}–${milestone.endStepNumber}`}
                  </span>
                  <div className="flex items-center gap-1">
                    {milestone.isRevisit && (
                      <SimpleTooltip content="Revisit: persona returned to this screen later in the run">
                        <span className="flex h-4 items-center gap-0.5 rounded bg-primary/10 px-1 text-xs font-medium text-primary-ink">
                          <RotateCcw className="h-2.5 w-2.5" />
                          <span>Revisit</span>
                        </span>
                      </SimpleTooltip>
                    )}
                  </div>
                </div>

                <div className="mt-0.5 flex items-baseline gap-1.5">
                  <span
                    className={cn(
                      "max-w-[180px] truncate text-xs font-semibold text-foreground",
                      isHighlighted && "text-primary",
                    )}
                    title={milestone.label}
                  >
                    {milestone.label}
                  </span>
                  {milestone.stepCount > 1 && (
                    <span className="text-xs text-muted-foreground">
                      ({milestone.stepCount} steps)
                    </span>
                  )}
                </div>
              </button>

              {index < milestones.length - 1 && (
                <ChevronRight
                  className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
